import {
  COLORS,
  FOOTER_H,
  HEADER_H,
  PAD,
  WIDTH,
  createCanvas,
  downloadCanvas,
  drawFooter,
  drawHeader,
  fill,
  fitText,
  loadLogo,
  roundRect,
  setFont,
  strokeBorder
} from './imageKit';

export type ReportItem = {
  nome: string;
  detalhe?: string;
};

export type ReportSection = {
  titulo: string;
  itens: ReportItem[];
  vazio?: string;
};

export type ReportStat = {
  label: string;
  valor: string;
  tom?: 'primary' | 'green' | 'red' | 'title';
};

type GenerateOptions = {
  ano: string;
  titulo: string;
  subtitulo?: string;
  stats?: ReportStat[];
  secoes: ReportSection[];
  rodape?: string;
  arquivo: string;
};

const STAT_H = 110;
const SECTION_TITLE_H = 54;
const SECTION_BOTTOM = 14;
const ROW_H = 56;
const SECTION_GAP = 20;
const COL_GAP = 24;
/** Acima disso a seção vira duas colunas, para a imagem não virar um rolo. */
const MAX_ROWS_UMA_COLUNA = 12;

const tons = {
  primary: COLORS.primary,
  green: COLORS.green,
  red: COLORS.red,
  title: COLORS.title
};

const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0])
    .join('')
    .toUpperCase() || '?';

const colunasDaSecao = (secao: ReportSection) => (secao.itens.length > MAX_ROWS_UMA_COLUNA ? 2 : 1);

const linhasDaSecao = (secao: ReportSection) =>
  Math.max(1, Math.ceil(secao.itens.length / colunasDaSecao(secao)));

const alturaDaSecao = (secao: ReportSection) =>
  SECTION_TITLE_H + linhasDaSecao(secao) * ROW_H + SECTION_BOTTOM;

export const generateReportImage = async ({
  ano,
  titulo,
  subtitulo,
  stats,
  secoes,
  rodape,
  arquivo
}: GenerateOptions) => {
  const temStats = Boolean(stats && stats.length);
  const alturaStats = temStats ? STAT_H + 24 : 0;
  const alturaSecoes = secoes.reduce((sum, secao) => sum + alturaDaSecao(secao), 0);
  const alturaGaps = Math.max(0, secoes.length - 1) * SECTION_GAP;
  const height = HEADER_H + 32 + alturaStats + alturaSecoes + alturaGaps + 24 + FOOTER_H;

  const logo = await loadLogo();
  const surface = createCanvas(height);
  if (!surface) return;
  const { canvas, ctx } = surface;

  drawHeader(ctx, logo, titulo, subtitulo || `Período ${ano}`);

  let y = HEADER_H + 32;

  // Indicadores
  if (stats && stats.length) {
    const gap = 16;
    const largura = (WIDTH - PAD * 2 - gap * (stats.length - 1)) / stats.length;
    stats.forEach((stat, index) => {
      const x = PAD + index * (largura + gap);
      roundRect(ctx, x, y, largura, STAT_H, 12);
      fill(ctx, COLORS.cardBg);
      strokeBorder(ctx, COLORS.border);
      ctx.textAlign = 'center';
      ctx.fillStyle = COLORS.muted;
      const label = fitText(ctx, stat.label, largura - 16, 700, 12, 9);
      ctx.fillText(label, x + largura / 2, y + 36);
      ctx.fillStyle = tons[stat.tom || 'title'];
      const valor = fitText(ctx, stat.valor, largura - 20, 800, 38, 18);
      ctx.fillText(valor, x + largura / 2, y + 82);
      ctx.textAlign = 'left';
    });
    y += STAT_H + 24;
  }

  // Seções
  secoes.forEach((secao, indiceSecao) => {
    const alturaSecao = alturaDaSecao(secao);
    roundRect(ctx, PAD, y, WIDTH - PAD * 2, alturaSecao, 12);
    fill(ctx, COLORS.cardBg);
    strokeBorder(ctx, COLORS.border);

    const x0 = PAD + 24;
    const innerW = WIDTH - PAD * 2 - 48;

    ctx.fillStyle = COLORS.title;
    const tituloSecao = fitText(ctx, secao.titulo, innerW - 90, 800, 20, 13);
    ctx.fillText(tituloSecao, x0, y + 34);

    // Contador da seção
    const contador = String(secao.itens.length);
    setFont(ctx, 800, 15);
    const contadorW = Math.max(38, ctx.measureText(contador).width + 24);
    roundRect(ctx, x0 + innerW - contadorW, y + 14, contadorW, 26, 13);
    fill(ctx, COLORS.badgeBg);
    ctx.fillStyle = COLORS.primaryDark;
    ctx.textAlign = 'center';
    ctx.fillText(contador, x0 + innerW - contadorW / 2, y + 32);
    ctx.textAlign = 'left';

    ctx.strokeStyle = COLORS.border;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x0, y + 46);
    ctx.lineTo(x0 + innerW, y + 46);
    ctx.stroke();

    if (secao.itens.length === 0) {
      ctx.fillStyle = COLORS.muted;
      setFont(ctx, 600, 15);
      ctx.fillText(secao.vazio || 'Nenhum aluno nesta lista.', x0, y + SECTION_TITLE_H + 30);
    }

    const colunas = colunasDaSecao(secao);
    const linhasPorColuna = linhasDaSecao(secao);
    const colW = (innerW - (colunas - 1) * COL_GAP) / colunas;

    secao.itens.forEach((item, index) => {
      const coluna = Math.floor(index / linhasPorColuna);
      const linha = index % linhasPorColuna;
      const x = x0 + coluna * (colW + COL_GAP);
      const rowY = y + SECTION_TITLE_H + linha * ROW_H;
      const centro = rowY + ROW_H / 2;

      if (linha % 2 === 0) {
        roundRect(ctx, x - 8, rowY + 3, colW + 16, ROW_H - 6, 8);
        fill(ctx, COLORS.softBg);
      }

      // Número da ordem
      ctx.fillStyle = COLORS.muted;
      setFont(ctx, 700, 13);
      ctx.textAlign = 'right';
      ctx.fillText(String(index + 1), x + 20, centro + 5);
      ctx.textAlign = 'left';

      // Avatar com iniciais
      const avatarX = x + 30;
      ctx.beginPath();
      ctx.arc(avatarX + 18, centro, 18, 0, Math.PI * 2);
      fill(ctx, COLORS.badgeBg);
      ctx.fillStyle = COLORS.primaryDark;
      setFont(ctx, 800, 13);
      ctx.textAlign = 'center';
      ctx.fillText(iniciais(item.nome), avatarX + 18, centro + 5);
      ctx.textAlign = 'left';

      // Nome e detalhe
      const textoX = avatarX + 48;
      const textoW = colW - (textoX - x) - 8;
      ctx.fillStyle = COLORS.title;
      const nome = fitText(ctx, item.nome, textoW, 700, 16, 11);
      ctx.fillText(nome, textoX, item.detalhe ? centro - 2 : centro + 5);
      if (item.detalhe) {
        ctx.fillStyle = COLORS.muted;
        const detalhe = fitText(ctx, item.detalhe, textoW, 600, 13, 10);
        ctx.fillText(detalhe, textoX, centro + 16);
      }
    });

    y += alturaSecao + (indiceSecao < secoes.length - 1 ? SECTION_GAP : 0);
  });

  const totalItens = secoes.reduce((sum, secao) => sum + secao.itens.length, 0);
  drawFooter(ctx, y + 24, rodape || `${totalItens} ${totalItens === 1 ? 'registro' : 'registros'}`);
  downloadCanvas(canvas, arquivo);
};
